import {Card, CardContent} from '@material-ui/core'
import {AxiosError} from 'axios'
import {get, isEmpty, pick} from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from 'react-query'

import {usePersistContact} from '../../../common/react-query/hooks/mutations/usePersistContact'
import {usePersistOrderRequests} from '../../../common/react-query/hooks/mutations/usePersistOrderRequests'
import {BusinessLineType} from '../../../common/types'
import {useOrderIntakeStyles} from '../../../Hooks/OrderIntake/useOrderIntakeStyles'
import {ConfirmationLegacyScreen} from '../../../OrderIntake/components/ConfirmationLegacyScreen'
import {DeliveryProcessingOrderLegacyCard} from '../../../OrderIntake/components/Deliver/DeliveryProcessingOrderLegacyCard'
import {ShippingType} from '../../../OrderIntake/declarations/OrderIntake.enums'
import {useBulkCementOrderIntake} from '../../../Organisms/OrderIntake/BulkCementOrderIntake.provider'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {PersistedOrderRequest} from '../../declarations/types'
import {OptionsKeys} from '../../Options'

interface HubErrorResponseData {
  detail: string
  status: string
  title: string
}
interface Props {
  businessLine: BusinessLineType
  onClose: () => void
}

export const DeliveryOrderIntakeRequestLegacyConfirmation: React.FC<Props> = ({
  businessLine,
  onClose
}) => {
  const {t} = useTranslation()
  const {deliveries, orderSummary} = useBulkCementOrderIntake()
  const classes = useOrderIntakeStyles()
  const queryClient = useQueryClient()
  const optionsReloaded = useRef(false)
  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>('')

  const {watch} = useFormContext<BulkOrderFormValues>()
  const selectedSite = watch('selectedSite')
  const mainPhone = get(deliveries[0], 'payload.contact.mainPhone', '')
  const backupPhone = get(deliveries[0], 'payload.contact.backupPhone', '')
  const userFullName = get(deliveries[0], 'payload.contact.fullName', '')
  const [showError, setShowError] = useState<boolean>(false)
  const siteNumber = watch('selectedSite.shippingAddress.siteNumber')
  const contact = watch('contact')
  const {mutate: persistContact} = usePersistContact()
  const {mutate: persistOrderRequests} = usePersistOrderRequests()
  useEffect(() => {
    if (isEmpty(deliveries)) return
    persistContact({siteNumber, contact})
    persistOrderRequests({
      siteNumber,
      orderRequests: deliveries.map((delivery) => ({
        ...(pick(delivery.payload, [
          'materialNumber',
          'materialEnteredNumber',
          'materialDescription',
          'capacity',
          'deliveryTime',
          'customerReference',
          'additionalDriverInfo',
          'contractItemPositionNumber',
          'isSendingConfirmationEmailUnChecked',
          'plantName'
        ]) as PersistedOrderRequest)
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries])
  const onItemError = (error: AxiosError) => {
    setShowError(true)

    // 'One or more errors occurred while creating the order, possible reasons: InvalidSlot'
    if (error.response?.data) {
      const errorData = error.response.data as HubErrorResponseData
      if (errorData.detail && errorData.detail.indexOf('InvalidSlot') > -1) {
        setCustomErrorMessage('invalidSlot')
        if (!optionsReloaded.current) {
          void queryClient.invalidateQueries(OptionsKeys.all())
          optionsReloaded.current = true
        }
      } else {
        setCustomErrorMessage(null)
      }
    }
  }
  return (
    <ConfirmationLegacyScreen
      data-test-id="delivery-order-intake-confirmation-screen"
      hideTitle={showError}
      summaryItems={deliveries.map((delivery, index) => {
        return (
          <Card
            variant="outlined"
            className={classes.confirmationSummaryItem}
            key={`Delivery-Item-Order-Confirmation-Processing-${delivery.payload.siteNumber}-${delivery.payload.materialEnteredNumber}-${index}`}
          >
            <CardContent>
              <DeliveryProcessingOrderLegacyCard
                businessLine={businessLine}
                shippingAddress={selectedSite ? selectedSite.shippingAddress : undefined}
                delivery={delivery}
                itemOrder={`${t('orderIntake.delivery')} ${index + 1}`}
                onError={onItemError}
                customErrorMessage={
                  customErrorMessage
                    ? t(`orderIntake.customErrorMessage.${customErrorMessage}`)
                    : undefined
                }
              />
            </CardContent>
          </Card>
        )
      })}
      date={orderSummary[0]?.createdOn}
      onClose={onClose}
      mainPhone={mainPhone}
      backupPhone={backupPhone}
      userFullName={userFullName}
      shippingType={ShippingType.DELIVER}
      itemsNumber={deliveries.length}
    />
  )
}
